import type {
  TransitionMeetingMyStudentsOverviewResource,
  TransitionMeetingMyStudentsOverviewResourceMeeting,
  TransitionMeetingStudentOverviewResource,
  TransitionMeetingSubjectRatingResource,
  LessonClassGroupResource,
} from "api/generated";
import type {
  TransitionMeetingMyStudentsOverview,
  TransitionMeetingStudentOverview,
  TransitionMeetingSubjectRating,
  LessonClassGroup,
  RatingGrade,
  RatingGradeType,
} from "meetings/advisory/types";
import { toAbsoluteImageUrl } from "shared/utils";
import { answerOptionFromDto } from "./answerOption.mapper";

const gradeFromDto = (dto: { type: string; grade: string }): RatingGrade => ({
  type: dto.type as RatingGradeType,
  grade: dto.grade,
});

const subjectRatingFromDto = (
  dto: TransitionMeetingSubjectRatingResource,
): TransitionMeetingSubjectRating => ({
  id: dto.id,
  subject: dto.subject,
  teacherShortage: dto.teacher_shortage,
  lessonClass: dto.lesson_class,
  rating: dto.rating,
  feedback: dto.feedback,
  grades: (dto.grades ?? []).map(gradeFromDto),
});

const studentOverviewFromDto = (
  dto: TransitionMeetingStudentOverviewResource,
): TransitionMeetingStudentOverview => ({
  id: dto.id,
  firstName: dto.first_name,
  middleName: dto.middle_name,
  lastName: dto.last_name,
  formattedName: dto.formatted_name,
  fullName: dto.full_name,
  mentorClass: dto.mentor_class,
  profileImage: toAbsoluteImageUrl(dto.profile_image),
  mentors: dto.mentors.map((m) => ({
    id: m.id,
    name: m.name,
    shortage: m.shortage,
  })),
  ratings: dto.ratings.map(subjectRatingFromDto),
  ratingOptions: dto.rating_options.map(answerOptionFromDto),
  meetingId: dto.meeting_id,
  meetingName: dto.meeting_name,
  progress: dto.progress,
});

export const lessonClassGroupFromDto = (
  dto: LessonClassGroupResource,
): LessonClassGroup => ({
  name: dto.name,
  studentAmount: dto.student_amount,
});

const meetingFromDto = (
  dto: TransitionMeetingMyStudentsOverviewResourceMeeting,
) => ({
  id: dto.id,
  name: dto.name,
  status:
    dto.status as TransitionMeetingMyStudentsOverview["meeting"]["status"],
  progressPercentage:
    dto.total_ratings > 0
      ? Math.round((dto.total_given_ratings / dto.total_ratings) * 100)
      : 0,
  closedModifyAt: dto.closed_modify_at ? new Date(dto.closed_modify_at) : null,
  ratingOptions: dto.rating_options.map(answerOptionFromDto),
  questionPrompt: dto.question_prompt,
  gradeSettings: {
    enableSeGrade: Boolean(dto.grade_settings?.enable_se_grade),
    enableRapportGrade: Boolean(dto.grade_settings?.enable_rapport_grade),
  },
});

export const myStudentsOverviewFromDto = (
  dto: TransitionMeetingMyStudentsOverviewResource,
): TransitionMeetingMyStudentsOverview => ({
  meeting: meetingFromDto(dto.meeting),
  students: dto.students.map(studentOverviewFromDto),
  meta: {
    hasMore: dto.meta.has_more,
    offset: dto.meta.offset,
    take: dto.meta.take,
    total: dto.meta.total,
  },
});
